import React, { useMemo } from 'react';
import useStyles from '../../theme/useStyles';
import { formatCurrency, formatDate } from '../../utils/format';
import { peakOf, percent } from '../../utils/summaryStats';
import {
  KpiCard, KpiGrid, SummaryColumns, TablePanel, BarCell, TotalRow, Badge
} from '../../components/ui';
import { assetSummary, WARRANTY_WINDOW } from './analytics';
import type { AssetLike, AllocationLike, MaintenanceLike, AssetGroup } from './analytics';

export interface AssetSummaryProps {
  assets: AssetLike[];
  allocations: AllocationLike[];
  maintenance: MaintenanceLike[];
  loading?: boolean;
  isDarkMode?: boolean;
}

const inDays = (days: number) =>
  days < 0 ? `${-days} day${days === -1 ? '' : 's'} overdue` : days === 0 ? 'Today' : `in ${days} day${days === 1 ? '' : 's'}`;

/** The Asset Management Summary tab: tiles, then report panels and watch-lists. */
const AssetSummaryView = ({ assets, allocations, maintenance, loading, isDarkMode }: AssetSummaryProps) => {
  const t = useStyles(isDarkMode).t;
  const s = useMemo(() => assetSummary(assets, allocations, maintenance), [assets, allocations, maintenance]);
  const nameOf = (id?: string) => assets.find((a) => a.recordId === id)?.name || id || '-';
  const monthPeak = peakOf(s.maintMonths, (r) => r.total);
  const maintPeak = peakOf(s.maintByAsset, (r) => r.total);
  const watch = [...s.serviceOverdue, ...s.serviceSoon];
  const money = (n: number) => formatCurrency(n);

  // Category, location and department share one layout: units, cost, book value.
  const groupPanel = (title: string, header: string, rows: AssetGroup[], color: string) => (
    <TablePanel
      title={title}
      isDarkMode={isDarkMode}
      loading={loading}
      columns={[
        { key: 'name', header, render: (r: AssetGroup) => <strong>{r.name}</strong> },
        { key: 'bar', header: 'Share of Cost', render: (r: AssetGroup) => <BarCell value={percent(r.cost, s.totalCost)} max={100} showPercent color={color} isDarkMode={isDarkMode} /> },
        { key: 'units', header: 'Items', align: 'right' as const },
        { key: 'book', header: 'Book Value', align: 'right' as const, nowrap: true, render: (r: AssetGroup) => money(r.book) }
      ]}
      rows={rows}
      rowKey="name"
      emptyMessage="No assets registered yet."
      below={rows.length ? <TotalRow label={`Total — ${s.units} items`} value={money(s.bookValue)} color={t.income} isDarkMode={isDarkMode} /> : null}
    />
  );

  return (
    <>
      <KpiGrid isDarkMode={isDarkMode}>
        <KpiCard label="Items Owned" value={`${s.units.toLocaleString('en-IN')} (${s.rows} rows)`} color={t.primary} isDarkMode={isDarkMode} />
        <KpiCard label="Church Investment" value={money(s.purchasedCost)} color={t.info} isDarkMode={isDarkMode} />
        <KpiCard label="Sponsored Value" value={money(s.sponsoredValue)} color={t.warning} isDarkMode={isDarkMode} />
        <KpiCard label="Current Book Value" value={money(s.bookValue)} color={t.income} isDarkMode={isDarkMode} />
        <KpiCard label="Depreciated So Far" value={money(s.depreciated)} color={t.expense} isDarkMode={isDarkMode} />
        <KpiCard label="Maintenance This Year" value={money(s.maintThisYear)} color={t.expense} isDarkMode={isDarkMode} />
        <KpiCard label="Service Overdue" value={String(s.serviceOverdue.length)} color={s.serviceOverdue.length ? t.danger : t.income} isDarkMode={isDarkMode} />
        <KpiCard label="Service Due (30 d)" value={String(s.serviceSoon.length)} color={s.serviceSoon.length ? t.warning : t.income} isDarkMode={isDarkMode} />
        <KpiCard label={`Warranty Ending (${WARRANTY_WINDOW} d)`} value={`${s.warrantySoon.length} of ${s.underWarranty}`} color={s.warrantySoon.length ? t.warning : t.income} isDarkMode={isDarkMode} />
        <KpiCard label="Transfers (90 d)" value={String(s.transfers.length)} color={t.info} isDarkMode={isDarkMode} />
      </KpiGrid>

      <SummaryColumns isDarkMode={isDarkMode}>
        <TablePanel
          title="Purchased vs Sponsored"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'name', header: 'Source', render: (r) => <strong>{r.name}</strong> },
            { key: 'bar', header: 'Share of Value', render: (r) => <BarCell value={percent(r.cost, s.totalCost)} max={100} showPercent color={r.name.startsWith('Sponsored') ? t.warning : t.info} isDarkMode={isDarkMode} /> },
            { key: 'units', header: 'Items', align: 'right' },
            { key: 'cost', header: 'Cost / Value', align: 'right', nowrap: true, render: (r) => money(r.cost) }
          ]}
          rows={s.sources}
          rowKey="name"
          emptyMessage="No assets registered yet."
          below={s.sources.length ? <TotalRow label="Total cost / value" value={money(s.totalCost)} isDarkMode={isDarkMode} /> : null}
        />
        {groupPanel('Assets by Category', 'Category', s.byCategory, t.primary)}
      </SummaryColumns>

      <SummaryColumns isDarkMode={isDarkMode}>
        {groupPanel('Assets by Location', 'Location', s.byLocation, t.info)}
        {groupPanel('Assets by Department', 'Department', s.byDepartment, t.info)}
      </SummaryColumns>

      <SummaryColumns isDarkMode={isDarkMode}>
        <TablePanel
          title="Maintenance Spend by Month"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'label', header: 'Month', nowrap: true, render: (r) => <strong>{r.label}</strong> },
            { key: 'bar', header: '', render: (r) => <BarCell value={r.total} max={monthPeak} color={t.expense} isDarkMode={isDarkMode} /> },
            { key: 'count', header: 'Jobs', align: 'right' },
            { key: 'total', header: 'Spent', align: 'right', nowrap: true, render: (r) => money(r.total) }
          ]}
          rows={s.maintMonths}
          rowKey="month"
          emptyMessage="No maintenance recorded yet."
          below={<TotalRow label="Last 12 months" value={money(s.maintMonths.reduce((n, m) => n + m.total, 0))} color={t.expense} isDarkMode={isDarkMode} />}
        />
        <TablePanel
          title="Maintenance by Asset"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'name', header: 'Asset', render: (r) => <strong>{r.name}</strong> },
            { key: 'bar', header: '', render: (r) => <BarCell value={r.total} max={maintPeak} color={t.expense} isDarkMode={isDarkMode} /> },
            { key: 'count', header: 'Jobs', align: 'right' },
            { key: 'total', header: 'Spent', align: 'right', nowrap: true, render: (r) => money(r.total) }
          ]}
          rows={s.maintByAsset.slice(0, 8)}
          rowKey="name"
          emptyMessage="No maintenance recorded yet."
          below={s.maintByAsset.length ? <TotalRow label="All maintenance" value={money(s.maintTotal)} color={t.expense} isDarkMode={isDarkMode} /> : null}
        />
      </SummaryColumns>

      <TablePanel
        title="Service Watch-list — Overdue & Next 30 Days"
        isDarkMode={isDarkMode}
        loading={loading}
        minWidth="520px"
        columns={[
          { key: 'nextServiceDate', header: 'Service Due', nowrap: true, render: (r) => <strong>{formatDate(r.nextServiceDate)}</strong> },
          { key: 'name', header: 'Asset', render: (r) => (<><strong>{r.name}</strong><br /><span style={{ fontSize: '12px', color: t.textFaint }}>{r.assetId}</span></>) },
          { key: 'location', header: 'Location', render: (r) => r.location || '-' },
          { key: 'vendor', header: 'Last Serviced By', render: (r) => r.vendor || '-' },
          { key: 'days', header: 'When', nowrap: true, render: (r) => <Badge tone={r.days < 0 ? 'error' : 'warning'}>{inDays(r.days)}</Badge> }
        ]}
        rows={watch}
        rowKey="assetId"
        emptyMessage="No asset is due for service in the next 30 days."
      />

      <SummaryColumns isDarkMode={isDarkMode}>
        <TablePanel
          title={`Warranties Ending — Next ${WARRANTY_WINDOW} Days`}
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'name', header: 'Asset', render: (r) => <strong>{r.name}</strong> },
            { key: 'category', header: 'Category', render: (r) => r.category || '-' },
            { key: 'warrantyExpiry', header: 'Expires', nowrap: true, render: (r) => <Badge tone="warning">{formatDate(r.warrantyExpiry)}</Badge> }
          ]}
          rows={s.warrantySoon}
          rowKey="assetId"
          emptyMessage="No warranty ends in this window."
        />
        <TablePanel
          title="Recent Transfers — Last 90 Days"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'date', header: 'Date', nowrap: true, render: (r) => <strong>{formatDate(r.date)}</strong> },
            { key: 'asset', header: 'Asset', render: (r) => nameOf(r.assetId) },
            { key: 'newLocation', header: 'Moved To', render: (r) => r.newLocation || '-' },
            { key: 'custodian', header: 'Custodian', render: (r) => r.custodian || '-' }
          ]}
          rows={s.transfers}
          rowKey={(r) => `${r.assetId}|${r.date}|${r.custodian}`}
          emptyMessage="No transfers in the last 90 days."
        />
      </SummaryColumns>
    </>
  );
};

export default AssetSummaryView;
